import React from 'react';
import ClientDataService from '../../../services/ClientData.service';
import TipContext from "../../../providers/GlobalTip";
import ReportService from '../../../services/reports/Report.service';
import BaseReport from '../../reports/BaseReport.component';
import { BaseReportRawData, BaseReportParsedData, MoneyDataItem } from '../../../interfaces/reports/BaseReport.interface';
import ReportTable from '../../reports/ReportTable.component';
import EpochMonthService from '../../../services/EpochMonth.service';
import OtbRecapReportService from '../../../services/reports/OtbRecapTableReport.service';
import moment from 'moment';
import * as XLSX from 'xlsx';
import 'moment-timezone';

const TODAY = moment().startOf('day');

// Column item
interface ColumnItem {
    name: string;
    sortable?: boolean;
    customFormatting?: (value: string | number) => JSX.Element
    customClassName?: string;
}

const HIERARCHY_OPTIONS = {
    "By Location": ["locationCode"],
    "By Class": ["classCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Class by Child": ["parentClientClassCode", "classCode"],
    "Location by Class by Child": ["locationCode", "parentClientClassCode", "classCode"],
    "Class by Location": ["classCode", "locationCode"],
}

type DataFile = {
    FileName: string;
    ProcessedDate: string;
    DataThroughDate: string;
    FinalizedDate: string|null;
}

interface ClientDataRaw extends BaseReportRawData {
    clientId: number;
    clientCode: string;
    locationClassId: number;
    parentClientClassCode: string;
    epochMonth: number;
    updatedDt: string;
    processDate: string;
    sales: number;
    markdowns: number;
    inventory: number;
    inventoryAdjustment: number;
    receivedRetail: number;
    receivedCost: number;
    returnsRetail: number;
    returnsCost: number;
    transfersIn: number;
    transfersOut: number;
    transfersInCost: number;
    transfersOutCost: number;
    filename: string;
    processedDate: string;
    createdDate: string;
    finalizedDate: string;
}

interface ClientOnOrderDataRaw extends BaseReportRawData {
    vertical: 'onOrderRetail' | 'onOrderCost';
    month1: number;
    month2: number;
    month3: number;
    month4: number;
    month5: number;
    month6: number;
    month7: number;
    month8: number;
    month9: number;
    month10: number;
    month11: number;
    month12: number;
}

interface ClientDataParsed extends BaseReportParsedData {
    sales: MoneyDataItem;
    markdowns: MoneyDataItem;
    inventory: MoneyDataItem;
    inventoryAdjustment: MoneyDataItem;
    receivedRetail: MoneyDataItem;
    receivedCost: MoneyDataItem;
    returnsRetail: MoneyDataItem;
    returnsCost: MoneyDataItem;
    transfersIn: MoneyDataItem;
    transfersOut: MoneyDataItem;
    transfersInCost: MoneyDataItem;
    transfersOutCost: MoneyDataItem;
}

interface ClientOnOrderDataParsed extends BaseReportParsedData {
    vertical: string;
    month1: MoneyDataItem;
    month2: MoneyDataItem;
    month3: MoneyDataItem;
    month4: MoneyDataItem;
    month5: MoneyDataItem;
    month6: MoneyDataItem;
    month7: MoneyDataItem;
    month8: MoneyDataItem;
    month9: MoneyDataItem;
    month10: MoneyDataItem;
    month11: MoneyDataItem;
    month12: MoneyDataItem;
}

interface DataTableProps {
    epochMonth: number;
    client: any;
    dataFile: DataFile | null;
}

const dataColumns = [
    { name: "Sales", sortable: true },
    { name: "Markdowns", sortable: true },
    { name: "Inventory", sortable: true },
    { name: "Inventory Adjustment", sortable: true },
    { name: "Received (Retail)", sortable: true },
    { name: "Received (Cost)", sortable: true },
    { name: "Returns (Retail)", sortable: true },
    { name: "Returns (Cost)", sortable: true },
    { name: "Transfers In (Retail)", sortable: true },
    { name: "Transfers Out (Retail)", sortable: true },
    { name: "Transfers In (Cost)", sortable: true },
    { name: "Transfers Out (Cost)", sortable: true }
] as ColumnItem[];

function parseDataForTable(rawData: ClientDataRaw[]): ClientDataParsed[] {
    return rawData.map(item => ({
        classCode: item.classCode,
        className: item.className,
        locationCode: item.locationCode,
        locationName: item.locationName,
        parentClientClassCode: item.parentClientClassCode,
        category: item.category ?? 'N/A',

        sales: new MoneyDataItem(item.sales),
        markdowns: new MoneyDataItem(item.markdowns),
        inventory: new MoneyDataItem(item.inventory),
        inventoryAdjustment: new MoneyDataItem(item.inventoryAdjustment),
        receivedRetail: new MoneyDataItem(item.receivedRetail),
        receivedCost: new MoneyDataItem(item.receivedCost),
        returnsRetail: new MoneyDataItem(item.returnsRetail),
        returnsCost: new MoneyDataItem(item.returnsCost),
        transfersIn: new MoneyDataItem(item.transfersIn),
        transfersOut: new MoneyDataItem(item.transfersOut),
        transfersInCost: new MoneyDataItem(item.transfersInCost),
        transfersOutCost: new MoneyDataItem(item.transfersOutCost),
    }));
}

function parsedOnOrderDataForTable(rawData: ClientOnOrderDataRaw[]): ClientOnOrderDataParsed[] {
    const vertMapping = {
        'onOrderRetail': 'Retail',
        'onOrderCost': 'Cost',
    }

    return rawData.map(item => ({
        classCode: item.classCode,
        className: item.className,
        locationCode: item.locationCode,
        locationName: item.locationName,
        parentClientClassCode: item.parentClientClassCode ?? null,
        category: item.category ?? 'N/A',
        vertical: vertMapping[item.vertical],

        month1: new MoneyDataItem(item.month1),
        month2: new MoneyDataItem(item.month2),
        month3: new MoneyDataItem(item.month3),
        month4: new MoneyDataItem(item.month4),
        month5: new MoneyDataItem(item.month5),
        month6: new MoneyDataItem(item.month6),
        month7: new MoneyDataItem(item.month7),
        month8: new MoneyDataItem(item.month8),
        month9: new MoneyDataItem(item.month9),
        month10: new MoneyDataItem(item.month10),
        month11: new MoneyDataItem(item.month11),
        month12: new MoneyDataItem(item.month12),
    }));
}

const DataTable: React.FC<DataTableProps> = ({ epochMonth, client, dataFile }) => {
    const clientId = client?.Id;
    const globalTip = TipContext.useMessage();
    const clientCode: string = client?.ClientCode || '';
    const [loading, setLoading] = React.useState<boolean>(false);

    // Raw data ref for reuse
    const rawDataRef = React.useRef<ClientDataRaw[]>([]);
    const rawOnOrderDataRef = React.useRef<any[]>([]);
    const [parsedData, setParsedData] = React.useState<ClientDataParsed[]>([]);
    const [onOrderParsedData, setOnOrderParsedData] = React.useState<ClientOnOrderDataParsed[]>([]);
    const [hierarchy, setHierarchy] = React.useState<string[] | null>(["locationCode", "classCode"]);
    const [maxProcessDate, setMaxProcessDate] = React.useState<moment.Moment|null>(null);

    const onOrderColumns = React.useMemo(() => ([
        { name: "On Order", sortable: false },
        { name: EpochMonthService.epochMonthToMMMYY(epochMonth), sortable: false, customClassName: "" },
        { name: EpochMonthService.epochMonthToMMMYY(epochMonth + 1), sortable: false, customClassName: "" },
        { name: EpochMonthService.epochMonthToMMMYY(epochMonth + 2), sortable: false, customClassName: "" },
        { name: EpochMonthService.epochMonthToMMMYY(epochMonth + 3), sortable: false, customClassName: "" },
        { name: EpochMonthService.epochMonthToMMMYY(epochMonth + 4), sortable: false, customClassName: "" },
        { name: EpochMonthService.epochMonthToMMMYY(epochMonth + 5), sortable: false, customClassName: "" },
        { name: EpochMonthService.epochMonthToMMMYY(epochMonth + 6), sortable: false, customClassName: "" },
        { name: EpochMonthService.epochMonthToMMMYY(epochMonth + 7), sortable: false, customClassName: "" },
        { name: EpochMonthService.epochMonthToMMMYY(epochMonth + 8), sortable: false, customClassName: "" },
        { name: EpochMonthService.epochMonthToMMMYY(epochMonth + 9), sortable: false, customClassName: "" },
        { name: EpochMonthService.epochMonthToMMMYY(epochMonth + 10), sortable: false, customClassName: "" },
        { name: EpochMonthService.epochMonthToMMMYY(epochMonth + 11), sortable: false, customClassName: "" },
    ] as ColumnItem[]), [epochMonth]);

    // Filters
    const [locationFilter, setLocationFilter] = React.useState<string[]>([]);
    const [classFilter, setClassFilter] = React.useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = React.useState<string[]>([]);
    const [showOnOrder, setShowOnOrder] = React.useState<boolean>(false);

    React.useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await ClientDataService.getClientDataForMonth({ clientId, epochMonth });
                const data = response?.data || [];
                rawDataRef.current = data;

                let maxProcessDate = null;
                for (const item of data) {
                    maxProcessDate = item.maxProcessDate?.date ? moment.utc(item.maxProcessDate.date).local() : null;
                    if (maxProcessDate)
                        break;
                }

                setMaxProcessDate(maxProcessDate);

                const onOrderData = response?.orderData || [];
                rawOnOrderDataRef.current = onOrderData;

                const parsed = parseDataForTable(data);
                setParsedData(parsed);

                const parsedOnOrder = parsedOnOrderDataForTable(onOrderData);
                setOnOrderParsedData(parsedOnOrder);
            } catch (error) {
                globalTip.show("Failed to fetch client data.");
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [clientId, epochMonth]);

    const hierarchyColumns = React.useMemo(() => {
        if (!hierarchy || hierarchy.length === 0) return [];
        let _hierarchyColumns = [] as ColumnItem[];
        for (let i = 0; i < (hierarchy?.length ?? 0); i++) {
            _hierarchyColumns.push({
                name: "",
                sortable: false,
            });
        }
        
        // append data columns
        if (showOnOrder) _hierarchyColumns = _hierarchyColumns.concat(onOrderColumns);
        else _hierarchyColumns = _hierarchyColumns.concat(dataColumns);

        return _hierarchyColumns;
    }, [hierarchy, showOnOrder, onOrderColumns]);

    const tableData = React.useMemo((): (string | number)[][] => {
        if (!parsedData || parsedData.length === 0) return [];
        if (!hierarchy || hierarchy.length === 0) return [];

        let _hierarchy = hierarchy;
        if (showOnOrder) {
            // Append 'vertical' to start of hierarchy
            _hierarchy = ['vertical', ...hierarchy];
        }

        // sort data by hierarchy first
        if (showOnOrder) {
            const sortedData = OtbRecapReportService.sortAndFilterData(
                onOrderParsedData,
                _hierarchy ?? ["locationCode", "classCode"],
                locationFilter,
                classFilter,
                categoryFilter
            ) as ClientOnOrderDataParsed[];

            const mappedData = sortedData.map((item) => {
                const labelRows = ReportService.hierarchyItemToVerticalDisplayNames(item, hierarchy ?? ["locationCode", "classCode"]);

                // if labelRows all = 'TOTAL' and item.vertical == '', skip this row
                if (labelRows.every(lr => lr === 'TOTAL') && item.vertical === '') return null;

                return [
                    ...labelRows,
                    item.vertical,
                    item.month1.formatted,
                    item.month2?.formatted ?? '',
                    item.month3?.formatted ?? '',
                    item.month4?.formatted ?? '',
                    item.month5?.formatted ?? '',
                    item.month6?.formatted ?? '',
                    item.month7?.formatted ?? '',
                    item.month8?.formatted ?? '',
                    item.month9?.formatted ?? '',
                    item.month10?.formatted ?? '',
                    item.month11?.formatted ?? '',
                    item.month12?.formatted ?? '',
                ] as (string | number)[];
            });
            return mappedData.filter((row): row is (string | number)[] => row !== null);
        } else {
            const sortedData = ReportService.sortAndFilterData(
                parsedData,
                _hierarchy ?? ["locationCode", "classCode"],
                locationFilter,
                classFilter,
                categoryFilter
            ) as ClientDataParsed[];

            return sortedData.map((item) => {
                const labelRows = ReportService.hierarchyItemToVerticalDisplayNames(item, hierarchy ?? ["locationCode", "classCode"]);

                return [
                    ...labelRows,
                    item.sales.formatted,
                    item.markdowns.formatted,
                    item.inventory.formatted,
                    item.inventoryAdjustment.formatted,
                    item.receivedRetail.formatted,
                    item.receivedCost.formatted,
                    item.returnsRetail.formatted,
                    item.returnsCost.formatted,
                    item.transfersIn.formatted,
                    item.transfersOut.formatted,
                    item.transfersInCost.formatted,
                    item.transfersOutCost.formatted,
                ] as (string | number)[];
            });
        }
    }, [parsedData, onOrderParsedData, hierarchy, locationFilter, classFilter, categoryFilter, showOnOrder]);

    const fileUpToDate = React.useMemo(() => {
        if (!dataFile) return false;
        if (dataFile.FinalizedDate !== null) return true;
        // if file processed date < maxProcessDate, up to date
        if (maxProcessDate) {
            const processedDate = moment(dataFile.ProcessedDate);
            return processedDate.isBefore(maxProcessDate);
        }
        return false;
    }, [dataFile, maxProcessDate]);

    const buildRawExportPayload = React.useCallback(() => {
        const retail = (rawDataRef.current || []) as ClientDataRaw[];
        const onOrder = (rawOnOrderDataRef.current || []) as ClientOnOrderDataRaw[];
        if (!retail.length && !onOrder.length) return null;

        const norm = (v: any) => String(v ?? '').trim();
        const period = EpochMonthService.epochMonthToYYYYMM(epochMonth);

        type MonthsArr = number[];
        const ooRetailMap = new Map<string, MonthsArr>();
        const ooCostMap = new Map<string, MonthsArr>();
        for (const oo of onOrder) {
            const key = `${norm(oo.locationCode)}||${norm(oo.classCode)}`;
            const months = [oo.month1,oo.month2,oo.month3,oo.month4,oo.month5,oo.month6,oo.month7,oo.month8,oo.month9,oo.month10,oo.month11,oo.month12].map(v => v || 0);
            if (oo.vertical === 'onOrderRetail') {
                const curr = ooRetailMap.get(key) || new Array(12).fill(0);
                ooRetailMap.set(key, curr.map((v,i) => v + (months[i] || 0)));
            } else if (oo.vertical === 'onOrderCost') {
                const curr = ooCostMap.get(key) || new Array(12).fill(0);
                ooCostMap.set(key, curr.map((v,i) => v + (months[i] || 0)));
            }
        }

        type OutRow = {
            locationCode: string; locationName: string;
            classCode: string; className: string; parentClientClassCode?: string | null;
            sales: number; markdowns: number; inventory: number; inventoryAdjustment: number;
            receivedRetail: number; receivedCost: number; returnsRetail: number; returnsCost: number;
            transfersIn: number; transfersOut: number; transfersInCost: number; transfersOutCost: number;
            onOrderRetail: number[]; onOrderCost: number[];
        };
        const out: OutRow[] = [];
        const retailKeys = new Set<string>();
        for (const item of retail) {
            const loc = norm(item.locationCode);
            const cls = norm(item.classCode);
            const key = `${loc}||${cls}`;
            retailKeys.add(key);
            out.push({
                locationCode: loc,
                locationName: norm(item.locationName),
                classCode: cls,
                className: norm(item.className),
                parentClientClassCode: item.parentClientClassCode ?? null,
                sales: item.sales || 0,
                markdowns: item.markdowns || 0,
                inventory: item.inventory || 0,
                inventoryAdjustment: item.inventoryAdjustment || 0,
                receivedRetail: item.receivedRetail || 0,
                receivedCost: item.receivedCost || 0,
                returnsRetail: item.returnsRetail || 0,
                returnsCost: item.returnsCost || 0,
                transfersIn: item.transfersIn || 0,
                transfersOut: item.transfersOut || 0,
                transfersInCost: item.transfersInCost || 0,
                transfersOutCost: item.transfersOutCost || 0,
                onOrderRetail: ooRetailMap.get(key) || new Array(12).fill(0),
                onOrderCost: ooCostMap.get(key) || new Array(12).fill(0),
            });
        }
        ooRetailMap.forEach((months, key) => {
            if (retailKeys.has(key)) return;
            const [loc, cls] = key.split('||');
            const anyOo = onOrder.find(oo => norm(oo.locationCode) === loc && norm(oo.classCode) === cls);
            out.push({
                locationCode: loc,
                locationName: anyOo ? norm(anyOo.locationName) : '',
                classCode: cls,
                className: anyOo ? norm(anyOo.className) : '',
                parentClientClassCode: anyOo?.parentClientClassCode ?? null,
                sales: 0, markdowns: 0, inventory: 0, inventoryAdjustment: 0,
                receivedRetail: 0, receivedCost: 0, returnsRetail: 0, returnsCost: 0,
                transfersIn: 0, transfersOut: 0, transfersInCost: 0, transfersOutCost: 0,
                onOrderRetail: months,
                onOrderCost: ooCostMap.get(key) || new Array(12).fill(0),
            });
        });
        ooCostMap.forEach((months, key) => {
            if (retailKeys.has(key)) return;
            if (ooRetailMap.has(key)) return;
            const [loc, cls] = key.split('||');
            const anyOo = onOrder.find(oo => norm(oo.locationCode) === loc && norm(oo.classCode) === cls);
            out.push({
                locationCode: loc,
                locationName: anyOo ? norm(anyOo.locationName) : '',
                classCode: cls,
                className: anyOo ? norm(anyOo.className) : '',
                parentClientClassCode: anyOo?.parentClientClassCode ?? null,
                sales: 0, markdowns: 0, inventory: 0, inventoryAdjustment: 0,
                receivedRetail: 0, receivedCost: 0, returnsRetail: 0, returnsCost: 0,
                transfersIn: 0, transfersOut: 0, transfersInCost: 0, transfersOutCost: 0,
                onOrderRetail: new Array(12).fill(0),
                onOrderCost: months,
            });
        });

        out.sort((a, b) => {
            const locA = parseInt(String(a.locationCode), 10);
            const locB = parseInt(String(b.locationCode), 10);
            const locCmp = (!isNaN(locA) && !isNaN(locB)) ? (locA - locB) : String(a.locationCode).localeCompare(String(b.locationCode));
            if (locCmp !== 0) return locCmp;
            const clsA = parseInt(String(a.classCode), 10);
            const clsB = parseInt(String(b.classCode), 10);
            const clsCmp = (!isNaN(clsA) && !isNaN(clsB)) ? (clsA - clsB) : String(a.classCode).localeCompare(String(b.classCode));
            return clsCmp;
        });

        const onOrderColLabels: string[] = [];
        for (let m = 0; m < 12; m++) {
            if (m === 0) {
                onOrderColLabels.push('OnOrderCost');
                onOrderColLabels.push('OnOrderRetail');
            } else {
                onOrderColLabels.push(`OnOrderCost${m}`);
                onOrderColLabels.push(`OnOrderRetail${m}`);
            }
        }

        const columns = [
            'ClientCode','LocationCode','ClassCode','Period',
            'Sales','Markdowns','Inventory','InventoryAdjustment','ReceivingRetail','ReceivingCost',
            'ReturnsRetail','ReturnsCost','TransfersInRetail','TransfersOutRetail','TransfersInCost','TransfersOutCost',
            ...onOrderColLabels, 
        ];

        // CSV data with ="..." wrappers for codes
        const csvData = out.map(r => {
            const interleavedOnOrder: number[] = [];
            for (let m = 0; m < 12; m++) {
                interleavedOnOrder.push(r.onOrderCost[m]);
                interleavedOnOrder.push(r.onOrderRetail[m]);
            }
            return [
                `="${clientCode}"`, `="${r.locationCode}"`, `="${r.classCode}"`, period,
                r.sales, r.markdowns, r.inventory, r.inventoryAdjustment, r.receivedRetail, r.receivedCost,
                r.returnsRetail, r.returnsCost, r.transfersIn, r.transfersOut, r.transfersInCost, r.transfersOutCost,
                ...interleavedOnOrder,
            ];
        });

        // Excel data with plain text codes
        const excelData = out.map(r => {
            const interleavedOnOrder: number[] = [];
            for (let m = 0; m < 12; m++) {
                interleavedOnOrder.push(r.onOrderCost[m]);
                interleavedOnOrder.push(r.onOrderRetail[m]);
            }
            return [
                clientCode, r.locationCode, r.classCode, period,
                r.sales, r.markdowns, r.inventory, r.inventoryAdjustment, r.receivedRetail, r.receivedCost,
                r.returnsRetail, r.returnsCost, r.transfersIn, r.transfersOut, r.transfersInCost, r.transfersOutCost,
                ...interleavedOnOrder,
            ];
        });
        
        const fileName = `Client_Raw_Data_${clientCode}_${EpochMonthService.epochMonthToMMMYY(epochMonth)}`;

        return { columns, csvData, excelData, fileName };
    }, [epochMonth, clientCode]);

    const exportToExcel = () => {
        const payload = buildRawExportPayload();
        if (!payload) { globalTip.show('No data to download'); return; }

        const { columns, excelData, fileName } = payload;
        const ws = XLSX.utils.aoa_to_sheet([columns, ...excelData]);
        
        // Format first 3 columns as text to preserve leading zeros and prevent scientific notation
        const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
        for (let R = range.s.r + 1; R <= range.e.r; ++R) {
            for (let C = 0; C <= 2; ++C) { // First 3 columns: ClientCode, LocationCode, ClassCode
                const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                if (!ws[cellAddress]) continue;
                ws[cellAddress].t = 's'; // Force text type
            }
        }
        
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        XLSX.writeFile(wb, `${fileName}.xlsx`);
    };

    const rowClassName = (rowIndex: number, sortedData?: Array<Array<string | number>>) => {
        const source = sortedData ?? tableData;
        if (!source || rowIndex < 0 || rowIndex >= source.length) return null;
        return ReportService.getRowClassName(source[rowIndex], hierarchy ?? ["locationCode", "classCode"]);
    };

    return (
        <div>
            {loading ? (
                <div>Loading data...</div>
            ) : (
                <div>
                    <BaseReport
                        title="Client Data Report"
                        tableColumns={hierarchyColumns.map(col => col.name)}
                        tableData={tableData as (string | number)[][]}
                        fileName={`Client_Data_Report_${new Date().toLocaleDateString()}`}
                        setHierarchy={setHierarchy}
                        hierarchy={hierarchy}
                        hierarchyOptions={HIERARCHY_OPTIONS}

                        parsedData={parsedData}
                        filteredLocationCodes={locationFilter}
                        filteredClassCodes={classFilter}
                        setFilteredLocationCodes={setLocationFilter}
                        setFilteredClassCodes={setClassFilter}
                        filteredCategories={categoryFilter}
                        setFilteredCategories={setCategoryFilter}

                        onApplyFilters={() => {
                            // Reparse
                            let parsedData = parseDataForTable(rawDataRef.current);
                            setParsedData(parsedData);
                            // let parsedParentData = processRawData(parentDataRef.current);
                            // setParentParsedData(parsedParentData);
                        }}
                    />
                    <div className="flex flex-row flex-wrap items-center space-x-4 mb-4 ml-8">
                        <div className="flex flex-col items-start">
                            <div className="border-orange-500 border-1 p-2">
                                <div className="flex flex-col space-y-1">
                                    {fileUpToDate ? (
                                        <span className="text-sm font-medium text-green-600">Data is up to date</span>
                                    ) : (
                                        <span className="text-sm font-medium text-red-600">
                                            Data is not up to date
                                        </span>
                                    )}
                                    <span className="text-xs text-gray-500">
                                        <strong>File Name:</strong> {dataFile?.FileName || 'N/A'}
                                    </span>
                                    {maxProcessDate && (
                                        <span className="text-xs text-gray-500">
                                            <strong>Latest Data Processed:</strong> {maxProcessDate.local().format('MM/DD/YYYY hh:mm:ss A')}
                                        </span>
                                    )}
                                    {dataFile?.ProcessedDate && (
                                        <span className="text-xs text-gray-500">
                                            <strong>Processed Date:</strong> {moment.tz(dataFile.ProcessedDate, 'America/Los_Angeles').local().format('MM/DD/YYYY hh:mm:ss A')}
                                        </span>
                                    )}
                                    {dataFile?.DataThroughDate && (
                                        <span className="text-xs text-gray-500">
                                            <strong>Data Through Date:</strong> {moment.tz(dataFile.DataThroughDate, 'America/Los_Angeles').local().format('MM/DD/YYYY')}
                                        </span>
                                    )}
                                    {dataFile?.FinalizedDate && (
                                        <span className="text-xs text-gray-500">
                                            <strong>Finalized Date:</strong> {moment.tz(dataFile.FinalizedDate, 'America/Los_Angeles').local().format('MM/DD/YYYY hh:mm:ss A')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Data Type</span>
                            <select
                                value={showOnOrder ? "On Order" : "Regular"}
                                onChange={(e) => setShowOnOrder(e.target.value === "On Order")}
                                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                <option value="Regular">Retail Data</option>
                                <option value="On Order">On Order</option>
                            </select>
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Raw Export</span>
                            <button
                                onClick={exportToExcel}
                                className="h-10 px-4 rounded-md text-[13px] font-semibold text-white bg-tw-primary hover:bg-sky-600 shadow transition"
                            >
                                Download Raw Excel
                            </button>
                        </div>
                    </div>
                    {/* Display table with export buttons */}
                    <ReportTable
                        columns={hierarchyColumns}
                        rowClassName={rowClassName}
                        data={tableData as (string | number)[][]}
                        defaultSortColumn="Sales"
                        defaultSortDirection="desc"
                        headerClassName="bg-tw-primary !text-white !text-center py-2 px-4"
                        // toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
                        // toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
                        stickyHeader={true}
                    />
                </div>
            )}
        </div>
    );
};

export default DataTable;